import React from 'react';
import QPureComponent from 'QPureComponent';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Field, Formik, Form } from 'formik';
import { List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import moment from 'moment';
import _ from 'lodash';
import DatePicker from 'react-datepicker';

import { withStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import * as scheduledTransactionsUtils from 'data/scheduledTransactions/scheduledTransactionsUtils';
import { mkScheduledTransaction, mkRecurrence, STTypeEnum } from 'data/scheduledTransactions/scheduledTransactionsTypes';
import * as scheduledTransactionsActions from 'data/scheduledTransactions/scheduledTransactionsActions';
import DownshiftField from 'components/QuickenControls/DownshiftField';
import AmountField from 'components/QuickenControls/AmountField';
import AccountField from 'components/QuickenControls/AccountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import TagsField from 'components/QuickenControls/TagsField';
import { updateNewTags } from 'data/tags/utils';
import { CashFlowTransaction } from 'data/transactions/types';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';
import * as categoriesSelectors from 'data/categories/selectors';
import { isBill } from 'data/chartOfAccounts/selectors';
import store from 'store';


const styles = (theme) => ({
  amtTxt: {
    textAlign: 'left',
  },
  categories: {
    inputField: {
      '& input': {
        '&::-ms-clear ': {
          display: 'none',
        },
      },
    },
  },
  checkbox: {
    margin: 0,
  },
  noPadding: {
    padding: 0,
  },
  expansionPanelSummary: {
    flexDirection: 'row-reverse',
    padding: 0,
  },
  expansionPanelSummaryContent: {
    paddingLeft: 12,
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.greyScale[5]}`,
    borderRadius: 4,
    padding: '6px 10px',
  },
  dateWidth: {
    width: '100%',
    '& .react-datepicker-wrapper': {
      width: '100%',
    },
    '& .react-datepicker__input-container': {
      width: '100%',
    },
  },
});

class ScheduledTransactionFormAcme extends QPureComponent {

  constructor(props) {
    super(props);

    this.rootRef = React.createRef();
    this.state = {
      focusField: '',
    };
  }

  cachedFormikScheduledTransaction = (scheduledTransaction) => this.formikScheduledTransaction(scheduledTransaction);

  formikScheduledTransaction(scheduledTransaction) {
    let formikScheduledTransaction;
    if (scheduledTransaction) {
      const frequencyAlias = scheduledTransactionsUtils.frequencyAliasFromRecurrence(scheduledTransaction.recurrence);
      const dueOnDate = DateTime.fromISO(scheduledTransaction.dueOn);
      const endOnDate = scheduledTransaction.recurrence && DateTime.fromISO(scheduledTransaction.recurrence.endOn);
      const virtualEndOn = endOnDate && endOnDate.isValid ? endOnDate.toLocaleString(DateTime.DATE_SHORT) : 'Never';
      const negator = scheduledTransactionsUtils.negatorFromCOA(scheduledTransaction.transaction && scheduledTransaction.transaction.coa);
      const virtualShowInitialSign = scheduledTransaction.transaction && scheduledTransaction.transaction.amount ? Math.sign(scheduledTransaction.transaction.amount) !== negator : undefined;
      const amount = scheduledTransaction.transaction && !Number.isNaN(scheduledTransaction.transaction.amount) ?
        String(negator < 0 ? Math.abs(scheduledTransaction.transaction.amount) : scheduledTransaction.transaction.amount) :
        undefined;
      const spreadTxn = scheduledTransaction.transaction && scheduledTransaction.transaction.toJS ? scheduledTransaction.transaction.toJS() : scheduledTransaction.transaction;
      const immutableTags = (scheduledTransaction.transaction && scheduledTransaction.transaction.tags) ?
        new ImmutableList(scheduledTransaction.transaction.tags) : undefined;
      formikScheduledTransaction = { // all modifiable props should be listed below (formik requires it)
        ...scheduledTransaction,
        dueOn: dueOnDate && dueOnDate.isValid ? dueOnDate.toLocaleString(DateTime.DATE_SHORT) : '',
        transaction: {
          payee: undefined,
          accountId: undefined,
          coa: undefined,
          ...spreadTxn,
          tags: immutableTags,
          amount,
        },
        virtualFrequency: frequencyAlias,
        virtualShowInitialSign,
        virtualEndOn,
      };
    }
    return formikScheduledTransaction;
  }

  formikSubmit = (values, actions) => {
    const { dispatchCreateScheduledTransactionAction, dispatchUpdateScheduledTransactionAction } = this.props;
    const dueOnMoment = values.dueOn && moment(values.dueOn);
    const dueOn = dueOnMoment && dueOnMoment.isValid() ? dueOnMoment.format('YYYY-MM-DD') : undefined;
    const endOnMoment = values.virtualEndOn && moment(values.virtualEndOn);
    const endOn = endOnMoment && endOnMoment.isValid() ? endOnMoment.format('YYYY-MM-DD') : '';
    const negator = scheduledTransactionsUtils.negatorFromCOA(values.transaction.coa);
    const amount = (values.transaction.amount.includes('+') || values.transaction.amount.includes('-')) ? Number(values.transaction.amount) : Number(values.transaction.amount) * negator;
    const count = undefined;
    const recurrence = values.virtualFrequency === scheduledTransactionsUtils.frequencyAliasTypes.custom ?
      { ...this.props.scheduledTransaction.recurrence, endOn } :
      scheduledTransactionsUtils.recurrenceFromFrequencyAlias(values.virtualFrequency, dueOn);

    if (values.id) {
      dispatchUpdateScheduledTransactionAction(mkScheduledTransaction({
        ...values,
        dueOn,
        recurrence: mkRecurrence({ endOn, count, ...recurrence }),
        transaction: updateNewTags(new CashFlowTransaction({ ...values.transaction, amount }), true),
      }));
    } else {
      dispatchCreateScheduledTransactionAction(mkScheduledTransaction({
        ...values,
        dueOn,
        recurrence: mkRecurrence({ endOn, count, ...recurrence }),
        transaction: updateNewTags(new CashFlowTransaction({ ...values.transaction, amount }), true),
      }));
    }

    actions.setSubmitting(false);
    this.setState({ isSubmitted: true });
  };

  renderForm = (formikProps) => {
    const { props } = this;
    const { classes } = props;
    if (this.props.render) {
      this.props.render({
        remove: () => this.props.dispatchDeleteScheduledTransactionAction(mkScheduledTransaction({
          clientId: formikProps.values.clientId,
          id: formikProps.values.id,
          isDeleted: true,
        })),
        submit: () => formikProps.submitForm(),
        isDirty: formikProps.dirty,
        isSubmitted: this.state.isSubmitted,
      });
    }

    return (
      <Form>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Field
              name="transaction.payee"
              validate={(value) => value && value.length > 0 ? undefined : 'required'}
              render={({ field, form: { touched, errors } }) =>
                <TextField
                  {...field}
                  id="payee"
                  label="Name"
                  className={classes.textField}
                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                  InputProps={{
                    autoComplete: 'off',
                  }}
                  margin="dense"
                  fullWidth
                  autoFocus
                  onFocus={(event) => event.target.select()}
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              name="transaction.amount"
              validate={(value) => value === undefined || value === null || value === '' ? 'required' : undefined}
              render={({ field, form: { touched, errors, values } }) => (
                <AmountField
                  {...field}
                  showInitialSign={values.virtualShowInitialSign}
                  showAmountAdornment
                  editable
                  omitCents={false}
                  currencySymbol="USD"
                  amountType="amount"
                  id="schedtxn-amount"
                  label="Recurring Amount"
                  margin="dense"
                  fullWidth
                  autoComplete="off"
                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                  className={classes.amtTxt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              name="virtualFrequency"
              validate={(value) => value && value.length > 0 ? undefined : 'required'}
              render={({ field: { onChange, ...field }, form: { values, touched, errors } }) =>
                <DownshiftField
                  key={_.get(values, field.name)}
                  autoFocus={this.state.focusField === field.name}
                  initialItemSelected={_.get(values, field.name)}
                  onSelected={(frequencyAlias) => {
                    formikProps.setFieldValue(field.name, frequencyAlias);
                    this.setState({focusField: field.name});
                    if (frequencyAlias === scheduledTransactionsUtils.frequencyAliasTypes.custom) {
                      // TODO: impelment custom
                    }
                  }}
                  items={scheduledTransactionsUtils.frequencyAliases}
                  textFieldProps={{
                    InputProps: {
                      ...field,
                    },
                    id: 'frequency',
                    label: 'Frequency',
                    margin: 'dense',
                    fullWidth: true,
                    autoComplete: 'off',
                    error: Boolean(_.get(touched, field.name) && _.get(errors, field.name)),
                    helperText: _.get(touched, field.name) && _.get(errors, field.name),
                  }}
                  lightWeight
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              name="dueOn"
              validate={(value) => {
                let errorMessage;
                if (!value) {
                  errorMessage = 'required';
                } else if (formikProps.initialValues.dueOn !== value) {
                  const dueOn = moment(value);
                  const now = moment().startOf('date');
                  if (!dueOn.isValid()) {
                    errorMessage = 'invalid date format';
                  } else if (dueOn < now) {
                    errorMessage = "can't change to past dates";
                  } else if (formikProps.values.virtualEndOn) {
                    const virtualEndOn = moment(formikProps.values.virtualEndOn);
                    if (virtualEndOn.isValid() && dueOn > virtualEndOn) {
                      errorMessage = `can't use dates after ${formikProps.values.virtualEndOn}`;
                    }
                  }
                }
                return errorMessage;
              }}
              render={({ field, form: { values, touched, errors } }) => {
                const date = moment(field.value);
                const selected = date.isValid() ? date : moment();
                return (
                  <div className={classes.dateWidth}>
                    <DatePicker
                      id="next-date"
                      popperPlacement="left-start"
                      selected={selected}
                      onChange={(momentDate) => {
                        if (momentDate) {
                          formikProps.setFieldValue(field.name, momentDate.format('l'));
                          this.rootRef.current.querySelector(`input#next-date`).focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                      minDate={moment().toDate()}
                      maxDate={moment(values.virtualEndOn).toDate()}
                      allowSameDay
                      disabledKeyboardNavigation
                      customInputRef="inputRef"
                      customInput={
                        <TextField
                          InputProps={{
                            ...field,
                            autoComplete: 'off',
                          }}
                          label="Next Date"
                          margin="dense"
                          fullWidth
                          error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                          helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                        />
                      }
                    />
                  </div>
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              name="transaction.accountId"
              validate={(value) => value && value.length > 0 ? undefined : 'required'}
              render={({ field: { onChange, ...field }, form: { values, touched, errors } }) =>
                <AccountField
                  key={_.get(values, field.name)}
                  onSelected={(account) => {
                    if (account) { // bugsnag check
                      if (!touched.transaction.isBill && !touched.transaction.isSubscription) {
                        if (isBill(store.getState(), account.id, values.transaction && values.transaction.coa)) {
                          if (!values.transaction.isSubscription) {
                            formikProps.setFieldValue('transaction.isBill', true);
                            formikProps.setFieldValue('transaction.isSubscription', false);
                          }
                        } else {
                          formikProps.setFieldValue('transaction.isBill', false);
                          formikProps.setFieldValue('transaction.isSubscription', false);
                        }
                      }
                      this.setState({focusField: field.name});
                      formikProps.setFieldValue(field.name, account ? account.id : undefined);
                    }
                  }}
                  initialAccountId={_.get(values, field.name)}
                  autoFocus={this.state.focusField === field.name}
                  textFieldProps={{
                    id: 'account',
                    label: 'Account',
                    margin: 'dense',
                    autoComplete: 'off',
                    fullWidth: true,
                    error: Boolean(_.get(touched, field.name) && _.get(errors, field.name)),
                    helperText: _.get(touched, field.name) && _.get(errors, field.name),
                    InputProps: {
                      ...field,
                    },
                  }}
                  lightWeight
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              name="transaction.coa"
              validate={(coa) => coa ? undefined : 'required'}
              render={({ field, form: { values, touched, errors } }) =>
                <CategoryField
                  {...field}
                  onChange={(coa) => {
                    if (!formikProps.touched.transaction.isExcludedFromReports) {
                      if (coa.type === CoaTypeEnum.ACCOUNT || coa.type === CoaTypeEnum.BALANCE_ADJUSTMENT) {
                        formikProps.setFieldValue('transaction.isExcludedFromReports', true);
                        formikProps.setFieldValue('transaction.isExcludedFromF2S', true);
                      } else {
                        formikProps.setFieldValue('transaction.isExcludedFromReports', false);
                        formikProps.setFieldValue('transaction.isExcludedFromF2S', false);
                      }
                    }

                    if (!touched.transaction.isBill && !touched.transaction.isSubscription) {
                      if (isBill(store.getState(), formikProps.values.transaction && formikProps.values.transaction.accountId, coa)) {
                        if (!values.transaction.isSubscription) {
                          formikProps.setFieldValue('transaction.isBill', true);
                          formikProps.setFieldValue('transaction.isSubscription', false);
                        }
                      } else {
                        formikProps.setFieldValue('transaction.isBill', false);
                        formikProps.setFieldValue('transaction.isSubscription', false);
                      }
                    }

                    const type = scheduledTransactionsUtils.typeFromCOA(coa);
                    if (type !== STTypeEnum.BILL || values.type !== STTypeEnum.SUBSCRIPTION) {
                      formikProps.setFieldValue('type', type);
                    }

                    formikProps.setFieldValue(field.name, coa);
                  }}
                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  label="Category"
                  editable
                  createEnabled
                  allowBlank
                  longCats={false}
                  margin="dense"
                  initialFocus={false}
                  autoFocus={false}
                  fontSize='16px'
                  inputStyle={classes.categories}
                  InputProps={{
                    id: 'transaction-input',
                    style: { height: undefined },
                  }}
                  textFieldProps={{
                    helperText: _.get(touched, field.name) && _.get(errors, field.name),
                  }}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <ExpansionPanel
              className={classes.noPadding}
              elevation={0}
              id="advance-dropdown-panel"
            >
              <ExpansionPanelSummary
                id="advance-dropdown-summary"
                classes={{
                  root: classes.expansionPanelSummary,
                  expandIcon: classes.noPadding,
                  content: classes.expansionPanelSummaryContent,
                }}
                expandIcon={<ExpandMoreIcon style={{ float: 'left' }}/>}
              >
                Advanced
              </ExpansionPanelSummary>
              <ExpansionPanelDetails className={classes.noPadding}>
                <Grid container spacing={4}>

                  <Grid item xs={12} sm={6}>
                    <Field
                      name="transaction.tags"
                      render={({ field, form: { touched, errors } }) =>
                        <TagsField
                          {...field}
                          onChange={(tags) => formikProps.setFieldValue(field.name, tags)}
                          error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                          helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                          label="Tags"
                          placeholder=" "
                          margin="dense"
                          id="transaction-tags"
                          fullWidth
                          autoFocus={false}
                        />
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      name="virtualEndOn"
                      validate={(value) => {
                        let errorMessage;
                        if (value !== 'Never' && value) {
                          const virtualEndOn = moment(value);
                          if (!virtualEndOn.isValid()) {
                            errorMessage = 'invalid date format';
                          } else if (formikProps.values.dueOn) {
                            const dueOn = moment(formikProps.values.dueOn);
                            if (dueOn.isValid() && virtualEndOn < dueOn) {
                              errorMessage = `can't use dates before ${formikProps.values.dueOn}`;
                            }
                          }
                        }
                        return errorMessage;
                      }}
                      render={({ field, form: { values, touched, errors } }) => {
                        const date = moment(field.value);
                        const selected = date.isValid() ? date : moment();
                        return (
                          <div className={classes.dateWidth}>
                            <DatePicker
                              id="end-date"
                              selected={selected}
                              onChange={(momentDate) => {
                                if (momentDate) {
                                  formikProps.setFieldValue(field.name, (momentDate).format('l'));
                                  this.rootRef.current.querySelector('input#end-date')?.focus();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  e.stopPropagation();
                                }
                              }}
                              minDate={moment(values.dueOn).toDate()}
                              allowSameDay
                              customInputRef="inputRef"
                              customInput={
                                <TextField
                                  InputProps={{
                                    ...field,
                                    onBlur: (e) => {
                                      if (!field.value) {
                                        formikProps.setFieldValue(field.name, 'Never');
                                      }
                                      field.onBlur(e);
                                    },
                                    autoComplete: 'off',
                                  }}
                                  label="End Date"
                                  margin="dense"
                                  fullWidth
                                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                                  helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                                />
                              }
                            />
                          </div>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <div className={classes.box}>
                      <Field
                        name="transaction.isBill"
                        render={({field, form: { values }}) =>
                          <FormControlLabel
                            {...field}
                            className={classes.checkbox}
                            control={<Checkbox/>}
                            label="Bill"
                            id="bill-checkbox"
                            checked={Boolean(field.value)}
                            onChange={(event, checked) => {
                              if (checked) {
                                formikProps.setFieldValue('transaction.isSubscription', false);
                                if (values.type === STTypeEnum.SUBSCRIPTION) {
                                  formikProps.setFieldValue('type', STTypeEnum.BILL);
                                }
                              }
                              field.onChange(event, checked);
                            }}
                          />
                        }
                      />

                      <Field
                        name="transaction.isSubscription"
                        render={({ field, form: { values }}) =>
                          <FormControlLabel
                            {...field}
                            className={classes.checkbox}
                            control={<Checkbox />}
                            label="Subscription"
                            id="subscription-checkbox"
                            checked={Boolean(field.value)}
                            onChange={(event, checked) => {
                              if (checked) {
                                formikProps.setFieldValue('transaction.isBill', false);
                                if (values.type === STTypeEnum.BILL) {
                                  formikProps.setFieldValue('type', STTypeEnum.SUBSCRIPTION);
                                }
                              }
                              field.onChange(event, checked);
                            }}
                          />
                        }
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <div className={classes.box}>
                      <Field
                        name="transaction.isExcludedFromReports"
                        render={({ field }) =>
                          <FormControlLabel
                            {...field}
                            className={classes.checkbox}
                            control={<Checkbox />}
                            label="Ignore from Reports"
                            id="ignore-checkbox"
                            checked={Boolean(field.value)}
                            onChange={(event, checked) => {
                              field.onChange(event, checked);
                              formikProps.setFieldValue('transaction.isExcludedFromF2S', checked);
                            }}
                          />
                        }
                      />

                      <Field
                        name="transaction.isExcludedFromF2S"
                        render={({ field }) =>
                          <FormControlLabel
                            {...field}
                            className={classes.checkbox}
                            control={<Checkbox />}
                            label="Ignore from Spending Plan"
                            id="ignore-spending-plan-checkbox"
                            checked={Boolean(field.value)}
                            disabled={formikProps.values.transaction.isExcludedFromReports && formikProps.values.transaction.isExcludedFromReports === field.value}
                          />
                        }
                      />
                    </div>
                  </Grid>

                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Grid>

        </Grid>

      </Form>
    );
  };

  render() {
    super.render();
    return (
      <div id="frm-root" ref={this.rootRef}>
        <Formik
          key={this.props.scheduledTransaction && (this.props.scheduledTransaction.id || this.props.scheduledTransaction.clientId)}
          initialValues={this.cachedFormikScheduledTransaction(this.props.scheduledTransaction)}
          validateOnChange
          onSubmit={this.formikSubmit}
          render={this.renderForm}
        />
      </div>
    );
  }
}

ScheduledTransactionFormAcme.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatchDeleteScheduledTransactionAction: PropTypes.func,
  dispatchCreateScheduledTransactionAction: PropTypes.func,
  dispatchUpdateScheduledTransactionAction: PropTypes.func,
  scheduledTransaction: PropTypes.object,
};


const mapDispatchToProps = (dispatch) => ({
  dispatchCreateScheduledTransactionAction: (data) => dispatch(scheduledTransactionsActions.createScheduledTransaction(data, { undo: { userMessage: 'Recurring transaction created.' } })),
  dispatchUpdateScheduledTransactionAction: (data) => dispatch(scheduledTransactionsActions.updateScheduledTransaction(data, { undo: { userMessage: 'Recurring transaction updated.' } })),
  dispatchDeleteScheduledTransactionAction: (data) => dispatch(scheduledTransactionsActions.deleteScheduledTransaction(data, { undo: { userMessage: 'Recurring transaction deleted.' } })),
});

const mapStateToProps = (state) => ({
  personalIncomeCategory: categoriesSelectors.getPersonalIncomeCategory(state),
});

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(mapStateToProps, mapDispatchToProps),
)(ScheduledTransactionFormAcme);
